import { useRef } from "react";
import { useSwipeable } from "react-swipeable";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/system";

interface SwipeButtonProps {
  onUnlock: () => void;
  text: string;
}

export function SwipeButton(props: SwipeButtonProps) {
  const { onUnlock, text } = props;
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up("sm"));

  const HEIGHT = isBigScreen ? 70 : 50;
  const WIDTH = isBigScreen ? 70 : 60;

  const ref = useRef<HTMLDivElement>();

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      if (!ref.current) return;

      const delta = eventData.deltaX;

      const minOffset = -4;
      const maxOffset =
        (ref.current.parentElement?.clientWidth || 0) - 8 - WIDTH;

      if (delta < minOffset) {
        ref.current.style.transform = `translateX(${minOffset}px)`;
        return;
      }
      if (delta > maxOffset) {
        ref.current.style.transform = `translateX(${maxOffset}px)`;
        return;
      }
      ref.current.style.transform = `translateX(${delta}px)`;
    },
    onSwiped: (eventData) => {
      if (!ref.current) return;

      const delta = eventData.deltaX;

      const parentWidth =
        (ref.current.parentElement?.clientWidth || 0) - 8 - WIDTH;

      if (delta > parentWidth * 0.8) {
        onUnlock();
      } else {
        ref.current.style.transform = `translateX(0px)`;
      }
    },
    trackMouse: true,
    trackTouch: true,
    preventScrollOnSwipe: true,
    touchEventOptions: {
      passive: true,
    },
  });

  const refPassthrough = (el: HTMLDivElement) => {
    handlers.ref(el);
    ref.current = el;
  };

  return (
    <Box
      component="div"
      sx={{
        height: HEIGHT,
        width: "75vw",
        left: "12.5%",
        bgcolor: "#111",
        position: "absolute",
        top: "70vh",
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        p: 0.5,
        boxShadow: "0 0 30px rgba(255, 255, 255, 0.2)",
        overflow: "hidden",
      }}
    >
      <Box
        component="div"
        sx={{
          bgcolor: "#555",
          height: "100%",
          position: "relative",
          width: WIDTH,
          minWidth: WIDTH,
          transition: "transform 0.1s",
          display: "flex",
          alignItems: "center",
          borderRadius: 2,
        }}
        {...handlers}
        ref={refPassthrough}
      >
        <KeyboardArrowRightIcon sx={{ width: "100%", height: "90%" }} />
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 400,
          width: "100%",
          textAlign: "center",
          background: "-webkit-linear-gradient(left, #333, #EEE)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
